import { useState } from 'react';
import { NavLink as MantineNavLink, Box, Divider, UnstyledButton } from '@mantine/core';
import { useLocation, useNavigate } from 'react-router-dom';
import { Settings, ChevronLeft, ChevronRight, CircleDollarSign, KeyRound, Lightbulb } from 'lucide-react';
import { FeatureKeys, type Permission } from '@bmai/shared';
import { useAuth } from '@/features/auth/AuthContext';
import { SURFACE_NAV_ICONS } from '@/shared/surfaces/SurfaceNavIcons';
import { categoryFromParam, surfaceForCategory } from '@/features/classification/ClassificationOptions';

/** When on `/classification/:category`, the surface path that owns it (so its nav item stays highlighted). */
function classificationSurfacePath(pathname: string): string | null {
    const match = /^\/classification\/([^/]+)/.exec(pathname);
    if (match === null) return null;
    const category = categoryFromParam(match[1]);
    const surfaceId = category !== undefined ? surfaceForCategory(category) : undefined;
    return surfaceId !== undefined ? `/${surfaceId}` : null;
}

interface NavItem {
    path: string;
    label: string;
    icon: React.ElementType;
    permission?: Permission;
    role?: string;
    /** Entitlement feature key that must be enabled for this surface to show. */
    feature?: string;
    children?: NavItem[];
}

const MAIN_NAV_ITEMS: NavItem[] = [
    { path: '/insights', label: 'Insights', icon: Lightbulb, permission: { action: 'view', resource: 'insights' }, feature: FeatureKeys.INSIGHTS },
    { path: '/ai-explorer', label: 'AI Explorer', icon: SURFACE_NAV_ICONS['ai-explorer'], permission: { action: 'view', resource: 'ai_explorer' }, feature: FeatureKeys.AI_EXPLORER },
    { path: '/behavior', label: 'Behavior', icon: SURFACE_NAV_ICONS.behavior, permission: { action: 'view', resource: 'behavior' }, feature: FeatureKeys.BEHAVIOR },
    { path: '/adoption', label: 'Adoption', icon: SURFACE_NAV_ICONS.adoption, permission: { action: 'view', resource: 'adoption' }, feature: FeatureKeys.ADOPTION },
    { path: '/token-usage', label: 'Token Usage', icon: SURFACE_NAV_ICONS['token-usage'], permission: { action: 'view', resource: 'token_usage' }, feature: FeatureKeys.TOKEN_USAGE },
    { path: '/cost', label: 'Cost', icon: CircleDollarSign, permission: { action: 'view', resource: 'cost' }, feature: FeatureKeys.COST },
    { path: '/governance', label: 'Governance', icon: SURFACE_NAV_ICONS.governance, permission: { action: 'view', resource: 'governance' }, feature: FeatureKeys.GOVERNANCE },
    { path: '/licenses', label: 'Licenses', icon: KeyRound, permission: { action: 'view', resource: 'licenses' }, feature: FeatureKeys.LICENSES },
];

const SETTINGS_NAV_ITEM: NavItem = { path: '/settings', label: 'Settings', icon: Settings };

/**
 * The tenant's default landing route — the first main-nav surface their plan +
 * permissions actually grant (so a free-plan tenant without Insights lands on
 * Licenses, not a feature they can't see). Falls back to Settings when no
 * surface is available. `hasFeature` fails open, so the default stays Insights
 * until entitlements are known.
 */
export function resolveDefaultRoute(
    hasFeature: (feature: string) => boolean,
    hasPermission: (permission: Permission) => boolean,
    hasRole: (role: string) => boolean,
): string {
    const first = MAIN_NAV_ITEMS.find((item) =>
        (!item.role || hasRole(item.role)) &&
        (!item.permission || hasPermission(item.permission)) &&
        (!item.feature || hasFeature(item.feature)),
    );
    return first?.path ?? '/settings/organization';
}

interface SidebarNavProps {
    collapsed: boolean;
    onToggleCollapse: () => void;
    /** Called after navigating to a route — used to close the mobile drawer. No-op on desktop. */
    onNavigate?: () => void;
    /** Whether to show the collapse/expand chevron at the rail's bottom. Hidden in the mobile overlay drawer. */
    showCollapseToggle?: boolean;
}

export default function SidebarNav({ collapsed, onToggleCollapse, onNavigate, showCollapseToggle = true }: SidebarNavProps): React.ReactElement {
    const location = useLocation();
    const navigate = useNavigate();
    const { hasPermission, hasRole, hasFeature } = useAuth();
    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
    const secondarySurface = classificationSurfacePath(location.pathname);

    const isVisible = (item: NavItem): boolean => {
        if (item.role && !hasRole(item.role)) return false;
        if (item.permission && !hasPermission(item.permission)) return false;
        if (item.feature && !hasFeature(item.feature)) return false;
        return true;
    };

    const collapsedIconStyles = {
        root: { display: 'flex', justifyContent: 'center', padding: '8px 0' },
        body: { flex: 'unset' },
        section: { marginRight: 0, marginInlineEnd: 0 },
    };

    function renderNavItem(item: NavItem): React.ReactElement {
        const Icon = item.icon;
        const childItems = item.children?.filter(isVisible) ?? [];

        if (childItems.length > 0) {
            const parentActive = location.pathname.startsWith(item.path);

            if (collapsed) {
                return (
                    <MantineNavLink
                        key={item.path}
                        leftSection={<Icon size={18} strokeWidth={parentActive ? 2.5 : 2} />}
                        active={parentActive}
                        title={item.label}
                        onClick={() => {
                            setOpenGroups((groups) => ({ ...groups, [item.path]: true }));
                            onToggleCollapse();
                        }}
                        variant="light"
                        mb={2}
                        styles={collapsedIconStyles}
                    />
                );
            }

            const activeChildPath = childItems
                .filter((child) => location.pathname === child.path || location.pathname.startsWith(`${child.path}/`))
                .sort((a, b) => b.path.length - a.path.length)[0]?.path;
            const opened = openGroups[item.path] ?? parentActive;

            return (
                <MantineNavLink
                    key={item.path}
                    label={item.label}
                    leftSection={<Icon size={18} strokeWidth={parentActive ? 2.5 : 2} />}
                    active={parentActive}
                    opened={opened}
                    onChange={(value) => setOpenGroups((groups) => ({ ...groups, [item.path]: value }))}
                    childrenOffset={28}
                    variant="light"
                    mb={2}
                    styles={{ label: { fontWeight: parentActive ? 700 : 500 } }}
                >
                    {childItems.map((child) => {
                        const ChildIcon = child.icon;
                        const childActive = child.path === activeChildPath;
                        return (
                            <MantineNavLink
                                key={child.path}
                                label={child.label}
                                leftSection={<ChildIcon size={16} strokeWidth={childActive ? 2.5 : 2} />}
                                active={childActive}
                                onClick={() => {
                                    navigate(child.path);
                                    onNavigate?.();
                                }}
                                variant="light"
                                mb={2}
                                styles={{ label: { fontWeight: childActive ? 700 : 500, fontSize: '13px' } }}
                            />
                        );
                    })}
                </MantineNavLink>
            );
        }

        const isActive = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`) || secondarySurface === item.path;

        return (
            <MantineNavLink
                key={item.path}
                label={collapsed ? undefined : item.label}
                leftSection={<Icon size={18} strokeWidth={isActive ? 2.5 : 2} />}
                active={isActive}
                onClick={() => {
                    navigate(item.path);
                    onNavigate?.();
                }}
                title={collapsed ? item.label : undefined}
                variant="light"
                mb={2}
                styles={{
                    root: collapsed ? collapsedIconStyles.root : undefined,
                    body: collapsed ? collapsedIconStyles.body : undefined,
                    section: collapsed ? collapsedIconStyles.section : undefined,
                    label: {
                        fontWeight: isActive ? 700 : 500,
                    },
                }}
            />
        );
    }

    return (
        <Box
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                position: 'relative',
            }}
        >
            {showCollapseToggle && (
                <UnstyledButton
                    onClick={onToggleCollapse}
                    aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
                    title={collapsed ? 'Expand' : 'Collapse'}
                    style={{
                        position: 'absolute',
                        top: '50%',
                        right: 0,
                        transform: 'translate(50%, -50%)',
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        background: 'var(--bm-color-body)',
                        border: '1px solid var(--bm-color-border)',
                        boxShadow: 'var(--bm-card-shadow)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--bm-color-text-dimmed)',
                        zIndex: 10,
                    }}
                >
                    {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
                </UnstyledButton>
            )}
            <Box style={{ flex: 1, display: 'flex', flexDirection: 'column' }} px={collapsed ? 4 : 12} pt="md">
                <Box>{MAIN_NAV_ITEMS.filter(isVisible).map(renderNavItem)}</Box>

                <Box mt="auto" pb="sm">
                    <Divider mb="sm" color="var(--bm-color-border)" />
                    {renderNavItem(SETTINGS_NAV_ITEM)}
                </Box>
            </Box>
        </Box>
    );
}
